import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from './Logo';
import Icon from './Icon';
import AICreditsDisplay from './AICreditsDisplay';
import { urlDoAvatar } from '../utils/avatar';
import { useIdioma } from '../i18n';
import { logoutRequest } from '../config/api';
import '../styles/AppShell.css';

/**
 * Menu lateral, agrupado por assunto.
 *
 * Os destinos vêm do menu que já existia. A referência propunha
 * `/dashboard`, `/runs`, `/settings/api`, `/contact` e `/documentation`, e
 * nenhuma das cinco é rota deste app — as duas primeiras existem com outro
 * nome, e as três últimas não existem. Manter o que ela sugeria daria cinco
 * links mortos num menu que a pessoa usa todo dia.
 */
const NAV = [
  { chave: 'menu.visaoGeral', itens: [{ to: '/user-home', chave: 'menu.painel', icon: 'grid' }] },
  {
    chave: 'menu.projetos',
    itens: [
      { to: '/projects', chave: 'menu.meusProjetos', icon: 'folder' },
      { to: '/create-project', chave: 'menu.criarProjeto', icon: 'plus' }
    ]
  },
  {
    chave: 'menu.conta',
    itens: [
      { to: '/settings', chave: 'menu.perfil', icon: 'user' },
      { to: '/payments', chave: 'menu.pagamentos', icon: 'card' },
      { to: '/plans', chave: 'menu.planoEUso', icon: 'gauge' }
    ]
  },
  { chave: 'menu.ajuda', itens: [{ to: '/docs', chave: 'menu.documentacao', icon: 'book' }] },
  {
    chave: 'menu.legal',
    itens: [
      { to: '/privacy-policy', chave: 'menu.privacidade', icon: 'shield' },
      { to: '/terms-of-service', chave: 'menu.termos', icon: 'book' }
    ]
  }
];

/** Barra inferior no celular: os quatro destinos mais usados. */
const ABAS = [
  { to: '/user-home', chave: 'menu.painel', icon: 'grid' },
  { to: '/projects', chave: 'menu.projetos', icon: 'folder' },
  { to: '/plans', chave: 'menu.planoEUso', icon: 'gauge' },
  { to: '/settings', chave: 'menu.perfil', icon: 'gear' }
];

/** Avatar relativo vem do servidor sem host; absoluto já vem pronto. */
/**
 * Moldura de todas as telas autenticadas.
 *
 *   <AppShell user={user} crumbs={['Projects', 'silentpuma.com']}>
 *     …conteúdo…
 *   </AppShell>
 */
const AppShell = ({ user, crumbs = [], actions, children }) => {
  const { t } = useIdioma();
  const { pathname } = useLocation();

  const ativo = (to) => pathname === to || (to !== '/user-home' && pathname.startsWith(to));

  /**
   * Sair de verdade exige o servidor: o cookie de sessão é httpOnly e só some
   * com o `Set-Cookie` de remoção que a API devolve. A referência trazia isto
   * como `<Link to="/logout">`, que navegaria para uma rota inexistente e
   * deixaria a pessoa autenticada na API.
   *
   * A recarga completa em vez de navegação pelo router é de propósito: garante
   * que nenhum estado em memória — usuário, projeto já carregado — sobreviva.
   */
  const sair = async () => {
    await logoutRequest();
    window.location.href = '/login';
  };

  return (
    <div className="app">
      <aside className="app-side">
        <Link to="/user-home" className="app-side-brand">
          <Logo size={26} />
        </Link>

        {NAV.map((grupo) => (
          <div className="app-nav-group" key={grupo.chave}>
            <span className="sp-label">{t(grupo.chave)}</span>
            {grupo.itens.map((i) => (
              <Link
                key={i.to}
                to={i.to}
                className={`app-nav-item ${ativo(i.to) ? 'is-active' : ''}`.trim()}
              >
                <Icon name={i.icon} size={17} />
                {t(i.chave)}
              </Link>
            ))}
          </div>
        ))}

        <div className="app-side-user">
          <img className="app-side-avatar" src={urlDoAvatar(user?.avatar)} alt="" />
          <div>
            <b>{user?.name}</b>
            {user?.plan && <span className="app-side-plan">{user.plan.toUpperCase()}</span>}
          </div>
          <button
            type="button"
            onClick={sair}
            className="sp-btn sp-btn-icon"
            aria-label={t('menu.sair')}
          >
            <Icon name="signOut" size={16} />
          </button>
        </div>
      </aside>

      <div className="app-body">
        <div className="app-top">
          <Link to="/user-home" className="app-top-brand">
            <Logo size={24} />
          </Link>

          <div className="app-crumbs">
            <Link to="/user-home">Silent Puma</Link>
            {crumbs.map((c, i) => (
              <React.Fragment key={c}>
                <i>/</i>
                {i === crumbs.length - 1 ? <b>{c}</b> : <span>{c}</span>}
              </React.Fragment>
            ))}
          </div>

          <div className="app-top-actions">
            {/* Os créditos vêm da API, não de um número fixo: a referência
                trazia `18/30` embutido no componente. */}
            <AICreditsDisplay />
            {actions}
          </div>
        </div>

        <main className="app-main">{children}</main>
      </div>

      <nav className="app-tabbar">
        {ABAS.map((aba) => (
          <Link
            key={aba.to}
            to={aba.to}
            className={`app-tab ${ativo(aba.to) ? 'is-active' : ''}`.trim()}
          >
            <Icon name={aba.icon} size={21} />
            <span>{t(aba.chave)}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
};

export default AppShell;
